/**
 * How wrong this browser's clock is (PRD-0010 D10, #259).
 *
 * The stage reports capture-to-arrival by subtracting a frame's `header.stamp`,
 * set on the robot, from `Date.now()` here. That spans two machines and carries
 * whatever their clocks disagree by. Measured against jetrover-002, both hosts
 * querying the SAME NTP server: the robot +7.5 ms, the operator's laptop
 * +183.8 ms. The stage read `~6 ms` for a link genuinely running near 180 ms.
 *
 * Fixing one laptop is a lab result. An operator's machine can be off by
 * anything, silently, so the console measures its OWN skew against a clock that
 * is actually disciplined — the API's — and subtracts it.
 *
 * This is the ordinary NTP estimate, over HTTPS:
 *
 *     offset ≈ ((t2 − t1) + (t3 − t4)) / 2
 *
 * with t1/t4 the local send and receive instants and t2 ≈ t3 the server's. It
 * assumes the round trip is roughly symmetric, which is why the BEST sample is
 * kept rather than the mean: the fastest exchange is the one least distorted by
 * a queue or a retransmit on one leg. Same reason NTP does it.
 */

import { apiFetch } from '../auth/apiFetch.ts';

/** How many probes per measurement. Enough to find a quiet one, few enough to
 *  be free. */
const PROBES = 5;

/** Beyond this the exchange was too noisy for its offset to mean anything, and
 *  half of it would land in the reading as error. */
const MAX_USABLE_RTT_MS = 1_000;

export interface ClockOffset {
  /** Milliseconds to ADD to `Date.now()` to get server time. */
  offsetMs: number;
  /** Round trip of the sample this came from — the error bar, roughly ±rtt/2. */
  rttMs: number;
  /** When it was measured, so staleness is visible to the caller. */
  atMs: number;
}

/**
 * Probe the API's clock and return the best estimate, or null if none of the
 * exchanges were clean enough to trust.
 *
 * Never throws: a console that cannot reach `/api/time` must fall back to
 * showing no correction, not to breaking the video stage.
 */
export async function measureClockOffset(
  signal?: AbortSignal,
): Promise<ClockOffset | null> {
  let best: ClockOffset | null = null;

  for (let i = 0; i < PROBES; i += 1) {
    if (signal?.aborted) break;
    try {
      const t1 = Date.now();
      // `no-store`: a cached response would carry a stale `t` and produce a
      // confident, badly wrong offset — worse than having none.
      // apiFetch, not fetch: every /api route sits behind the gateway's
      // Cognito authorizer, and a bare fetch gets a fast, tidy 401 — which
      // this function would report as "offset unmeasurable" forever while
      // looking like it was working.
      const res = await apiFetch('/api/time',
        { cache: 'no-store', signal: signal ?? null });
      const t4 = Date.now();
      if (!res.ok) continue;
      const body = (await res.json()) as { t?: unknown };
      if (typeof body.t !== 'number' || !Number.isFinite(body.t)) continue;

      const rttMs = t4 - t1;
      if (rttMs > MAX_USABLE_RTT_MS) continue;
      const offsetMs = ((body.t - t1) + (body.t - t4)) / 2;
      if (best === null || rttMs < best.rttMs) {
        best = { offsetMs: Math.round(offsetMs), rttMs, atMs: t4 };
      }
    } catch {
      // Aborted, offline, or unparseable. Try the next probe; if they all fail
      // the caller gets null and simply applies no correction.
    }
  }

  return best;
}
